import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Linkedin, Mail, Award, Users, TrendingUp, Globe } from "lucide-react";
import Amaresh from "@/assets/Amaresh.png";
import Aswin from "@/assets/Aswin.png";
import GraceYu from "@/assets/Grace Yu.png";
import Harshitha from "@/assets/Harshitha.png";
// Import professional teamwork images
import teamworkCollaborationImage from "@/assets/team-workspace.jpg";
import teamMeetingImage from "@/assets/professional-meeting.jpg";

const ExpandableBio = ({ bio }: { bio: string }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Split bio into sentences and take first 2 for preview
  const sentences = bio.split(". ");
  const previewText =
    sentences.slice(0, 2).join(". ") + (sentences.length > 2 ? "." : "");
  const hasMoreContent = sentences.length > 2;

  return (
    <div className="text-muted-foreground mb-4 text-sm leading-relaxed">
      <p className="whitespace-pre-line">{isExpanded ? bio : previewText}</p>
      {hasMoreContent && (
        <Button
          variant="link"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-0 h-auto text-primary hover:text-primary/80 text-xs mt-1"
        >
          {isExpanded ? "Show less" : "Show more"}
        </Button>
      )}
    </div>
  );
};

const Leadership = () => {
  const leaders = [
    {
      name: "Amaresh S A",
      position: "Chief Executive Officer - EMEA",
      image: Amaresh,
      bio: "Worked with Fortune 20+ clients SAP, Capgemini, PRGX, Lavante , Encora ,Mavi, Mamenta ,etc, delivering tailored solutions and strategic technology insights and enhance their competitive edge. My experience includes successful engagements with industry leaders, ensuring their specific needs are met with precision and expertise. \n \n Develop and communicate a clear technology vision and strategy aligned with the organization’s business goals and objectives. \n \n Stay abreast of industry trends, emerging technologies, and best practices to ensure the organization remains competitive and innovative. Define and enforce technical standards, best practices, and coding guidelines to maintain consistency and quality across projects. Conduct regular code reviews and architectural assessments to ensure compliance with standards and identify areas for improvement",
      expertise: [
        "Strategic Planning",
        "Enterprise Solutions",
        "Technology Leadership",
      ],
      achievements: [
        "20+ years industry experience",
        "Led multiple enterprise transformations",
        "Built global delivery teams",
      ],
      linkedin: "https://www.linkedin.com/in/amaresh-s-a-0a6502112",
      email: "amaresh.a@smartborn.in",
    },
    {
      name: "Aswin Palaniappa",
      position: "Associate Vice President - APAC",
      image: Aswin,
      bio: "Aswin Palaniappa specializes in Industrial Internet of Things (IIoT) with focus on transforming industries through advanced technologies. He addresses challenges in IIoT adoption by emphasizing seamless connectivity and robust cybersecurity. His work involves identifying scalable solutions that enable organizations to leverage IIoT for efficiency improvements. As a mentor, he develops technical leaders capable of navigating the evolving IIoT landscape through collaboration and continuous learning.",
      expertise: ["IIoT", "AI/ML", "DevOps", "Cybersecurity"],
      achievements: [
        "IIoT implementation expertise",
        "Cloud architecture experience",
        "DevOps transformation lead",
      ],
      linkedin: "https://in.linkedin.com/in/aswinpalaniappa",
      email: "aswin.palaniappa@smartborn.in",
    },
    {
      name: "Grace Yu",
      position: "Operation Lead - Germany",
      image: GraceYu,
      bio: "I lead operational excellence in Germany by blending strategic leadership with deep expertise in DevOps. With a strong focus on scalability, automation, and efficiency, I ensure that teams are empowered to deliver high-performing, reliable systems that drive business success. \n \n My approach is rooted in continuous improvement—optimizing workflows, streamlining infrastructure, and fostering a culture of collaboration between development and operations. I bridge the gap between innovation and execution, aligning technical strategies with organizational goals to accelerate delivery without compromising on quality. \n \n Whether it’s building CI/CD pipelines, managing cloud-native deployments, or ensuring operational resilience, I bring clarity and precision to complex challenges. At the core of my work is a commitment to empowering teams, refining processes, and delivering results that move the needle. \n \n Let’s build systems that don’t just work—but work better, faster, and smarter.",
      expertise: ["Operations Management", "DevOps", "Cloud Infrastructure"],
      achievements: [
        "Operations leadership in EMEA",
        "DevOps transformation expertise",
        "Process optimization specialist",
      ],
      linkedin: "https://in.linkedin.com/company/smartborn",
      email: "contact@smartborn.io",
    },
    {
      name: "Harshitha R",
      position: "HR Head",
      image: Harshitha,
      bio: "Harshitha R leads human resources with focus on aligning talent strategies with company objectives. She emphasizes employee engagement, performance management, and regulatory compliance. Her responsibilities include cultivating an inclusive workplace culture and building high-performing teams to support organizational growth.",
      expertise: ["HR Management", "Talent Acquisition", "Employee Relations"],
      achievements: [
        "HR operations leadership",
        "Team scaling from 50 to 200+",
        "Talent management frameworks",
      ],
      linkedin: "https://www.linkedin.com/in/harshitha-r-6b1309169",
      email: "harshita.r@smartborn.in",
    },
  ];

  const stats = [
    { icon: Users, label: "Total Team Members", value: "200+" },
    { icon: TrendingUp, label: "Projects Delivered", value: "180+" },
    { icon: Globe, label: "Countries Served", value: "15+" },
    { icon: Award, label: "Industry Recognition", value: "12+ Awards" },
  ];

  return (
    <section
      id="leadership"
      className="py-12 md:py-16 lg:py-20 bg-gradient-to-br from-background via-background/50 to-muted/20 relative"
    >
      {/* Background Image */}
      <div className="absolute inset-0 opacity-5">
        <img
          src={teamworkCollaborationImage}
          alt="Professional teamwork background"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-primary bg-clip-text text-transparent pb-2 px-4">
            Meet Our Executive Leadership
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto px-4">
            Visionary leaders with decades of combined experience in technology,
            innovation, and global operations. Our executive team drives
            strategic direction and ensures excellence in every project.
          </p>
        </div>

        {/* Leadership Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12 md:mb-16">
          {stats.map((stat, index) => (
            <Card
              key={index}
              className="text-center p-6 bg-gradient-card border-0 shadow-card hover:shadow-elegant transition-all duration-300 hover:-translate-y-1"
            >
              <CardContent className="p-0">
                <stat.icon className="h-8 w-8 mx-auto mb-3 text-primary" />
                <div className="text-2xl font-bold text-foreground">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground">
                  {stat.label}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Leadership Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {leaders.map((leader, index) => (
            <Card
              key={index}
              className="group hover:shadow-elegant transition-all duration-300 hover:-translate-y-1 overflow-hidden bg-gradient-card border-0 shadow-card"
            >
              <CardContent className="p-6 md:p-8">
                <div className="flex flex-col sm:flex-row gap-6">
                  {/* Profile Image */}
                  <div className="flex-shrink-0">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center overflow-hidden">
                      <img
                        src={leader.image}
                        alt={leader.name}
                        className="w-20 h-20 rounded-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Leader Info */}
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-foreground mb-1">
                          {leader.name}
                        </h3>
                        <p className="text-primary font-semibold">
                          {leader.position}
                        </p>
                      </div>
                      <div className="flex gap-2 mt-2 sm:mt-0">
                        <a
                          href={leader.linkedin}
                          target="_blank"
                          className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
                        >
                          <Linkedin className="h-4 w-4" />
                        </a>
                        <a
                          href={`mailto:${leader.email}`}
                          target="_blank"
                          className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
                        >
                          <Mail className="h-4 w-4" />
                        </a>
                      </div>
                    </div>

                    <ExpandableBio bio={leader.bio} />

                    {/* Expertise */}
                    <div className="mb-4">
                      <h4 className="font-semibold text-sm text-foreground mb-2">
                        Core Expertise
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {leader.expertise.map((skill, skillIndex) => (
                          <Badge
                            key={skillIndex}
                            variant="secondary"
                            className="text-xs"
                          >
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Achievements */}
                    <div>
                      <h4 className="font-semibold text-sm text-foreground mb-2">
                        Key Achievements
                      </h4>
                      <ul className="space-y-1">
                        {leader.achievements.map((achievement, achIndex) => (
                          <li
                            key={achIndex}
                            className="text-xs text-muted-foreground flex items-center"
                          >
                            <Award className="h-3 w-3 mr-2 text-primary flex-shrink-0" />
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Leadership;
